if (!customElements.get('collection-infinite-scroll')) {
  customElements.define(
    'collection-infinite-scroll',
    class CollectionInfiniteScroll extends HTMLElement {
      connectedCallback() {
        if (this.initialized) return;
        this.initialized = true;
        this.loading = false;
        this.productGrid = document.getElementById('product-grid');
        this.onScroll = this.onScroll.bind(this);
        this.onPageHide = this.onPageHide.bind(this);

        if (!this.productGrid) return;

        if ('scrollRestoration' in history) {
          history.scrollRestoration = 'manual';
        }

        this.init();
      }

      disconnectedCallback() {
        this.observer?.disconnect();
        this.stopSavingScroll();
      }

      async init() {
        const currentPage = Number(this.dataset.page || 1);
        const totalPages = Number(this.dataset.pages || 1);
        const priceSort = this.getPriceSortDirection();

        // Price sort must see every product before rendering. Shopify paginates
        // first, so a cheap item on page 2 would otherwise appear late (or jump
        // to the top mid-scroll). Load all pages, sort once, then stop.
        if (priceSort && totalPages > 1) {
          await this.loadAllPagesForPriceSort(totalPages);
          requestAnimationFrame(() => {
            this.restoreScrollPosition();
            this.startSavingScroll();
          });
          return;
        }

        if (currentPage > 1) {
          await this.hydratePages(currentPage);
        } else {
          this.revealGrid();
        }

        requestAnimationFrame(() => {
          this.restoreScrollPosition();
          this.startSavingScroll();
        });

        if (!this.dataset.url) {
          this.finish();
          return;
        }

        this.observer = new IntersectionObserver(
          (entries) => {
            if (entries.some((entry) => entry.isIntersecting)) {
              this.loadMore();
            }
          },
          { rootMargin: '600px 0px' },
        );

        this.observer.observe(this);
      }

      getSpinner() {
        return this.querySelector('.loading__spinner');
      }

      setLoading(isLoading) {
        this.loading = isLoading;
        this.getSpinner()?.classList.toggle('hidden', !isLoading);
      }

      getStorageKey() {
        const url = new URL(window.location.href);
        url.searchParams.delete('page');
        url.searchParams.delete('section_id');
        const query = url.searchParams.toString();
        return `collection-scroll:${url.pathname}${query ? `?${query}` : ''}`;
      }

      getViewportAnchor() {
        const nodes = this.productGrid.querySelectorAll('[data-product-id]');
        const headerOffset = 80;

        for (const node of nodes) {
          const target = node.closest('li') || node;
          const rect = target.getBoundingClientRect();

          if (rect.bottom > headerOffset) {
            return {
              productId: node.getAttribute('data-product-id'),
              topOffset: rect.top,
            };
          }
        }

        return null;
      }

      saveScrollPosition() {
        const anchor = this.getViewportAnchor();
        if (!anchor?.productId) return;

        try {
          sessionStorage.setItem(this.getStorageKey(), JSON.stringify(anchor));
        } catch (error) {
          // Ignore quota / private mode errors
        }
      }

      restoreScrollPosition() {
        let saved;

        try {
          saved = JSON.parse(sessionStorage.getItem(this.getStorageKey()) || 'null');
        } catch (error) {
          saved = null;
        }

        if (!saved?.productId) return;

        const node = this.productGrid.querySelector(
          `[data-product-id="${saved.productId}"]`,
        );
        if (!node) return;

        const target = node.closest('li') || node;
        const topOffset =
          typeof saved.topOffset === 'number' ? saved.topOffset : 80;
        const top = target.getBoundingClientRect().top + window.scrollY;

        window.scrollTo(0, Math.max(0, top - topOffset));
      }

      onScroll() {
        if (this.scrollSaveTimer) return;

        this.scrollSaveTimer = window.setTimeout(() => {
          this.scrollSaveTimer = null;
          this.saveScrollPosition();
        }, 150);
      }

      onPageHide() {
        this.saveScrollPosition();
      }

      startSavingScroll() {
        this.saveScrollPosition();
        window.addEventListener('scroll', this.onScroll, { passive: true });
        window.addEventListener('pagehide', this.onPageHide);
      }

      stopSavingScroll() {
        window.removeEventListener('scroll', this.onScroll);
        window.removeEventListener('pagehide', this.onPageHide);
        if (this.scrollSaveTimer) {
          window.clearTimeout(this.scrollSaveTimer);
          this.scrollSaveTimer = null;
        }
      }

      buildPageUrl(page) {
        const sectionId = this.productGrid.dataset.id;
        const url = new URL(window.location.href);
        url.searchParams.delete('section_id');

        if (page <= 1) {
          url.searchParams.delete('page');
        } else {
          url.searchParams.set('page', String(page));
        }

        url.searchParams.set('section_id', sectionId);
        return url;
      }

      async fetchGridItems(page) {
        const response = await fetch(this.buildPageUrl(page).toString());
        if (!response.ok) throw new Error(`HTTP ${response.status}`);

        const html = new DOMParser().parseFromString(
          await response.text(),
          'text/html',
        );
        const newGrid = html.getElementById('product-grid');
        if (!newGrid) return [];

        return Array.from(newGrid.children).map((item) => {
          this.prepareItem(item);
          return item;
        });
      }

      getPriceSortDirection() {
        const sortBy = new URL(window.location.href).searchParams.get('sort_by');
        if (sortBy === 'price-ascending') return 'asc';
        if (sortBy === 'price-descending') return 'desc';
        return null;
      }

      getProductId(item) {
        return (
          item.querySelector('[data-product-id]')?.getAttribute('data-product-id') ||
          item.getAttribute('data-price') ||
          null
        );
      }

      /**
       * Fetch every paginated page, sort all products by price, then render once.
       * Inserts (video / image) stay at the same product-slot positions as page 1.
       */
      async loadAllPagesForPriceSort(totalPages) {
        this.setLoading(true);
        this.productGrid.classList.add('product-grid--hydrating');
        this.productGrid.style.opacity = '0';

        try {
          const pageNumbers = Array.from(
            { length: totalPages },
            (_, index) => index + 1,
          );
          const pageItems = await Promise.all(
            pageNumbers.map((page) => this.fetchGridItems(page)),
          );

          const page1 = pageItems[0] || [];
          const videoInsert = page1.find((el) =>
            el.classList.contains('double_grid_item'),
          );
          const imageInsert = page1.find(
            (el) =>
              !el.hasAttribute('data-price') &&
              !el.classList.contains('double_grid_item'),
          );

          const products = [];
          const seen = new Set();

          pageItems.forEach((items) => {
            items.forEach((item) => {
              if (!item.hasAttribute('data-price')) return;

              const id = this.getProductId(item);
              if (id) {
                if (seen.has(id)) return;
                seen.add(id);
              }

              this.prepareItem(item);
              products.push(item);
            });
          });

          const ascending = this.getPriceSortDirection() === 'asc';
          products.sort((a, b) => {
            const priceA = Number(a.getAttribute('data-price')) || 0;
            const priceB = Number(b.getAttribute('data-price')) || 0;
            return ascending ? priceA - priceB : priceB - priceA;
          });

          const fragment = document.createDocumentFragment();
          products.forEach((product, index) => {
            // Match Liquid: inserts before products at 1-based indexes 7 and 12
            if (index === 6 && videoInsert) {
              this.prepareItem(videoInsert);
              fragment.appendChild(videoInsert);
            }
            if (index === 11 && imageInsert) {
              this.prepareItem(imageInsert);
              fragment.appendChild(imageInsert);
            }
            fragment.appendChild(product);
          });

          this.productGrid.replaceChildren(fragment);
          this.activateNewItems();
          this.revealGrid();
          this.clearPageFromHistory();
          this.finish();
        } catch (error) {
          console.error('Price sort full load failed:', error);
          this.revealGrid();
        } finally {
          this.setLoading(false);
        }
      }

      clearPageFromHistory() {
        const historyUrl = new URL(window.location.href);
        historyUrl.searchParams.delete('page');
        historyUrl.searchParams.delete('section_id');

        const searchParams = historyUrl.searchParams.toString();
        if (typeof FacetFiltersForm !== 'undefined') {
          FacetFiltersForm.searchParamsPrev = searchParams;
        }

        window.history.replaceState(
          { searchParams },
          '',
          `${historyUrl.pathname}${searchParams ? `?${searchParams}` : ''}`,
        );
      }

      prepareItem(item, { appended = false } = {}) {
        item
          .querySelectorAll('.scroll-trigger')
          .forEach((element) =>
            element.classList.add('scroll-trigger--cancel'),
          );
        if (item.classList.contains('scroll-trigger')) {
          item.classList.add('scroll-trigger--cancel');
        }
        if (appended) {
          item.classList.add('product-grid-item--appended');
        }
      }

      activateNewItems() {
        if (typeof window.initProductCardScrollers === 'function') {
          window.initProductCardScrollers(this.productGrid);
        }

        this.productGrid.querySelectorAll('quick-add-modal').forEach((modal) => {
          if (typeof modal.show !== 'function') {
            customElements.upgrade(modal);
          }
        });
      }

      revealGrid() {
        this.productGrid.style.opacity = '';
        this.productGrid.removeAttribute('style');
        this.productGrid.classList.remove('product-grid--hydrating');
        this.productGrid.classList.add('product-grid--ready');
      }

      async hydratePages(currentPage) {
        this.setLoading(true);
        window.scrollTo(0, 0);

        try {
          // Rebuild pages 1..current in one pass so nothing pops in mid-view
          const pageNumbers = Array.from(
            { length: currentPage },
            (_, index) => index + 1,
          );
          const pageItems = await Promise.all(
            pageNumbers.map((page) => this.fetchGridItems(page)),
          );

          const fragment = document.createDocumentFragment();
          pageItems.flat().forEach((item) => fragment.appendChild(item));

          if (fragment.childNodes.length) {
            this.productGrid.replaceChildren(fragment);
            this.activateNewItems();
          }

          this.revealGrid();
        } catch (error) {
          console.error('Collection infinite scroll hydrate failed:', error);
          this.revealGrid();
        } finally {
          this.setLoading(false);
        }
      }

      async loadMore() {
        if (this.loading || !this.dataset.url || !this.productGrid) return;

        const sectionId = this.productGrid.dataset.id;
        if (!sectionId) return;

        this.setLoading(true);
        let shouldContinue = false;

        try {
          const url = new URL(this.dataset.url, window.location.origin);
          url.searchParams.set('section_id', sectionId);

          const response = await fetch(url.toString());
          if (!response.ok) throw new Error(`HTTP ${response.status}`);

          const html = new DOMParser().parseFromString(
            await response.text(),
            'text/html',
          );
          const newGrid = html.getElementById('product-grid');
          if (!newGrid) {
            this.finish();
            return;
          }

          const fragment = document.createDocumentFragment();
          Array.from(newGrid.children).forEach((item) => {
            this.prepareItem(item, { appended: true });
            fragment.appendChild(item);
          });

          this.productGrid.appendChild(fragment);
          this.activateNewItems();
          this.updateHistory(url);
          this.saveScrollPosition();

          const next = html.querySelector('collection-infinite-scroll');
          if (next?.dataset.url) {
            this.dataset.url = next.dataset.url;
            if (next.dataset.page) this.dataset.page = next.dataset.page;
            shouldContinue = true;
          } else {
            this.finish();
            return;
          }
        } catch (error) {
          console.error('Collection infinite scroll failed:', error);
        } finally {
          this.setLoading(false);
        }

        // Observer may not re-fire if the sentinel never left the viewport
        if (
          shouldContinue &&
          this.isConnected &&
          this.dataset.url &&
          this.isNearViewport()
        ) {
          this.loadMore();
        }
      }

      finish() {
        this.dataset.url = '';
        this.observer?.disconnect();
        this.setAttribute('hidden', '');
        this.setLoading(false);
      }

      isNearViewport() {
        const rect = this.getBoundingClientRect();
        return rect.top < window.innerHeight + 600;
      }

      updateHistory(fetchedUrl) {
        const historyUrl = new URL(fetchedUrl.toString());
        historyUrl.searchParams.delete('section_id');

        const searchParams = historyUrl.searchParams.toString();
        if (typeof FacetFiltersForm !== 'undefined') {
          FacetFiltersForm.searchParamsPrev = searchParams;
        }

        window.history.replaceState(
          { searchParams },
          '',
          `${historyUrl.pathname}${searchParams ? `?${searchParams}` : ''}`,
        );
      }
    },
  );
}
